import { ChevronRightIcon } from "@heroicons/react/24/solid";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/core/components/ui/badge";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import {
  BUSINESS_SETTINGS_ROUTES,
  SIDEBAR_MODES,
  SIDEBAR_SECTIONS,
} from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { useBusiness } from "@/core/business";
import { cn } from "@/core/utils/utils";
import { getCustomerNameOrPhone } from "@/customer/utils";
import { REFERRAL_FIELD } from "@/referrals/constants";
import { REFERRAL_FIELD_TO_SORTER } from "@/referrals/constants";
import { IReferral } from "@/referrals/referral";

const REFERRAL_STATUS_TO_BADGE = {
  LEAD: "New",
  IN_CONTACT: "In progress",
  SCHEDULED: "Scheduled",
  CLOSED: "Closed",
  LOST: "Lost",
  PAID: "Paid",
};

interface ReferralListItemProps {
  referral: IReferral;
  isFinal: boolean;
}

const ReferralListItem = ({ referral, isFinal }: ReferralListItemProps) => {
  const navigate = useNavigate();
  const { businessId } = useBusiness();
  const { updateSidebar } = useBusinessSettingsSidebar();

  return (
    <Stack
      direction="row"
      justify="between"
      align="center"
      fullWidth
      className={cn(
        "h-12 cursor-pointer",
        !isFinal && "border-b border-stroke-tertiary",
      )}
      onClick={() => {
        navigate(
          `/business-settings/${businessId}${BUSINESS_SETTINGS_ROUTES.referralHistory}`,
        );
        updateSidebar({
          section: SIDEBAR_SECTIONS.referralHistory,
          mode: SIDEBAR_MODES.view,
          selectedItemId: referral.id,
          isOpen: true,
        });
      }}
    >
      <Typography variant="bodyW2">
        {getCustomerNameOrPhone(referral.potential_customer)}
      </Typography>
      <Stack direction="row" align="center">
        <Badge variant={referral.status}>
          {REFERRAL_STATUS_TO_BADGE[referral.status]}
        </Badge>
        <ChevronRightIcon className="h-6 w-6" />
      </Stack>
    </Stack>
  );
};

interface ReferralListProps {
  referrals: IReferral[];
  isLoading: boolean;
  title?: string;
  hideHeader?: boolean;
}

export const ReferralList = ({
  referrals,
  isLoading,
  title = "Assignments",
  hideHeader,
}: ReferralListProps) => {
  const sortedReferrals = useMemo(
    () => [...referrals].sort(REFERRAL_FIELD_TO_SORTER[REFERRAL_FIELD.status]),
    [referrals],
  );

  return (
    <Stack className="h-full px-6 pr-1" fullWidth>
      {!hideHeader && (
        <Stack
          direction="row"
          justify="between"
          align="center"
          fullWidth
          className="flex-none pr-5"
        >
          <Typography variant="subtitleW2">
            {title} {isLoading ? "" : `(${referrals?.length})`}
          </Typography>
          {/* <PlusIcon className="h-6 w-6 cursor-pointer text-interactive-text" /> */}
        </Stack>
      )}

      {!referrals.length && (
        <Typography
          variant="bodyW2"
          textColor="tertiary"
          className={cn("w-full text-center", hideHeader && "mt-4")}
        >
          No referrals assigned
        </Typography>
      )}
      <ScrollArea
        className={cn("min-h-0 w-full flex-1 pr-5", hideHeader && "mt-4")}
      >
        <Stack className="pb-6" gap="none" fullWidth>
          {sortedReferrals.map((referral, index) => (
            <ReferralListItem
              key={referral.id}
              referral={referral}
              isFinal={index === sortedReferrals.length - 1}
            />
          ))}
        </Stack>
      </ScrollArea>
    </Stack>
  );
};
